import React, { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { CalendarIcon } from '@heroicons/react/24/outline';
import PublicLayoutWrapper from '../components/PublicLayoutWrapper';
import ClassCard from '../components/ClassCard';
import ClassScheduleExpanded from '../components/ClassScheduleExpanded';
import GuestSignupModal from '../components/GuestSignupModal';
import { useClassData } from '../hooks/useClassData';
import { ClassBookingService } from '../Services/classBookingService.ts';
import { calculateCycleDates } from '../utils/classUtils';

const PublicClasses = () => {
    const { venueId } = useParams();
    const { classes: rawClasses, loading } = useClassData(true, venueId); // venueId can be ID or short key

    // Ensure classes is always an array
    const classes = useMemo(() => {
        if (!rawClasses) return [];
        if (Array.isArray(rawClasses)) return rawClasses;
        // If it's an object, try to extract an array property
        if (typeof rawClasses === 'object') {
            // Common API response patterns
            if (rawClasses.data && Array.isArray(rawClasses.data)) return rawClasses.data;
            if (rawClasses.classes && Array.isArray(rawClasses.classes)) return rawClasses.classes;
            if (rawClasses.items && Array.isArray(rawClasses.items)) return rawClasses.items;
        }
        console.warn('Classes data is not in expected format:', rawClasses);
        return [];
    }, [rawClasses]);

    // State management
    const [expandedClasses, setExpandedClasses] = useState(new Set());
    const [bulkSelectedDates, setBulkSelectedDates] = useState([]);
    const [showGuestModal, setShowGuestModal] = useState(false);
    const [selectedClass, setSelectedClass] = useState(null);
    const [selectedDatesForBooking, setSelectedDatesForBooking] = useState([]);
    const [guestSignupLoading, setGuestSignupLoading] = useState(false);

    // Services
    const bookingService = new ClassBookingService(true);

    // Handlers
    const toggleClassExpansion = (classId) => {
        const newExpanded = new Set(expandedClasses);
        if (newExpanded.has(classId)) {
            newExpanded.delete(classId);
        } else {
            newExpanded.add(classId);
        }
        setExpandedClasses(newExpanded);
    };

    const handleBulkDateToggle = (classId, date) => {
        const key = `${classId}-${date.toISOString()}`;
        if (bulkSelectedDates.includes(key)) {
            setBulkSelectedDates(bulkSelectedDates.filter(d => d !== key));
        } else {
            setBulkSelectedDates([...bulkSelectedDates, key]);
        }
    };

    const getClassSelectedDates = (classId) => {
        return bulkSelectedDates.filter(key => key.startsWith(`${classId}-`));
    };

    const handleBookClass = (venueClass) => {
        setSelectedClass(venueClass);

        // For recurring classes, check if any dates are selected
        if (venueClass.cycleType !== undefined) {
            const classSelectedDates = getClassSelectedDates(venueClass.id);
            if (classSelectedDates.length > 0) {
                // Convert selected dates to ISO strings for booking
                const dates = classSelectedDates.map(key => key.substring(`${venueClass.id}-`.length));
                setSelectedDatesForBooking(dates);
            } else {
                setSelectedDatesForBooking([]);
            }
        } else {
            setSelectedDatesForBooking([]);
        }

        setShowGuestModal(true);
    };

    const handleGuestSignup = async (email) => {
        if (!selectedClass) return;

        setGuestSignupLoading(true);

        try {
            const result = await bookingService.createGuestUserAndBook(
                email,
                venueId,
                selectedClass.id,
                selectedDatesForBooking.length > 0 ? selectedDatesForBooking : null
            );

            if (result.success) {
                // Store guest token temporarily
                if (result.guestToken) {
                    localStorage.setItem('wh-guest-token', result.guestToken);
                }

                setShowGuestModal(false);

                if (result.paymentUrl) {
                    window.location.href = result.paymentUrl;
                } else {
                    alert(result.message || 'Account created and class booked successfully!');
                }
            } else {
                alert(result.error || 'Failed to create booking. Please try again.');
            }
        } catch (error) {
            console.error('Error during guest signup:', error);
            alert('An error occurred. Please try again.');
        } finally {
            setGuestSignupLoading(false);
        }
    };

    const handleClearSelection = (classId) => {
        setBulkSelectedDates(bulkSelectedDates.filter(key => !key.startsWith(`${classId}-`)));
    };

    const handleBulkBooking = (classId) => {
        const venueClass = classes.find(c => c.id === classId);
        if (venueClass) {
            handleBookClass(venueClass);
        }
    };

    // Add validation for venueId (can be ID or short key)
    if (!venueId) {
        return (
            <PublicLayoutWrapper title="Error">
                <div className="text-center py-12">
                    <CalendarIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
                    <p className="text-red-400 text-lg">Venue identifier is required to view classes.</p>
                    <p className="text-gray-400 text-sm mt-2">Please check the URL and try again.</p>
                </div>
            </PublicLayoutWrapper>
        );
    }

    if (loading) {
        return (
            <PublicLayoutWrapper title="Public Classes">
                <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-400"></div>
                </div>
            </PublicLayoutWrapper>
        );
    }

    return (
        <PublicLayoutWrapper
            title="Public Classes"
            subtitle="Join our classes as a guest - no account required"
        >
            {classes.length === 0 ? (
                <div className="text-center py-12">
                    <CalendarIcon className="h-16 w-16 text-gray-500 mx-auto mb-4" />
                    <p className="text-gray-400 text-lg">No public classes available at this time.</p>
                </div>
            ) : (
                <div className="space-y-6">
                    {/* Info Banner */}
                    <div className="bg-blue-900/20 border border-blue-600 rounded-lg p-4">
                        <h3 className="text-blue-400 font-medium mb-2">Guest Booking</h3>
                        <p className="text-blue-300 text-sm">
                            Book classes as a guest with just your email address. We'll create a temporary account
                            for you that can be upgraded later to access additional features.
                        </p>
                    </div>

                    {/* Class Cards */}
                    {classes.map(venueClass => {
                        const allDates = venueClass.cycleType !== undefined
                            ? calculateCycleDates(venueClass.startDate, venueClass.cycleType, 12, venueClass.expirationDate)
                            : [new Date(venueClass.startDate)];

                        return (
                            <ClassCard
                                key={venueClass.id}
                                venueClass={venueClass}
                                isExpanded={expandedClasses.has(venueClass.id)}
                                onToggleExpansion={toggleClassExpansion}
                                onBookClass={handleBookClass}
                                isPublic={true}
                                showUserStatus={false}
                            >
                                {/* Expanded Schedule View for recurring classes */}
                                {venueClass.cycleType !== undefined && (
                                    <ClassScheduleExpanded
                                        venueClass={venueClass}
                                        allDates={allDates}
                                        bulkSelectedDates={bulkSelectedDates}
                                        onDateToggle={handleBulkDateToggle}
                                        onClearSelection={handleClearSelection}
                                        onBulkBooking={handleBulkBooking}
                                        getClassSelectedDates={getClassSelectedDates}
                                        showUserBookings={false}
                                    />
                                )}
                            </ClassCard>
                        );
                    })}
                </div>
            )}

            {/* Guest Signup Modal */}
            <GuestSignupModal
                isOpen={showGuestModal}
                onClose={() => setShowGuestModal(false)}
                onGuestSignup={handleGuestSignup}
                selectedClass={selectedClass}
                selectedDates={selectedDatesForBooking}
                isLoading={guestSignupLoading}
            />
        </PublicLayoutWrapper>
    );
};

export default PublicClasses;
